/**
 * FilterButton - Toolbar button that triggers filter panel
 *
 * Core component: No internal dependencies.
 * Just a button with filter icon and optional badge.
 * Use with FilterDropdowns (composed) for the full filter system.
 */

import { Filter } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FilterButtonProps {
  /** Click handler - typically toggles the category box */
  onClick: () => void;
  /** Whether the filter panel is currently open */
  isActive?: boolean;
  /** Number of active filters (shows badge when > 0) */
  activeCount?: number;
  /** Disabled state */
  disabled?: boolean;
}

export function FilterButton({
  onClick,
  isActive,
  activeCount,
  disabled,
}: FilterButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'p-3 rounded-full bg-white flex-shrink-0',
        'shadow-[0_2px_8px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]',
        'text-slate-500 active:bg-slate-50 transition-all',
        'focus:outline-none focus:ring-2 focus:ring-sky-500',
        isActive && 'ring-2 ring-sky-500',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      aria-label="Filter options"
      aria-expanded={isActive}
    >
      <div className="relative">
        <Filter className="h-5 w-5" />
        {activeCount !== undefined && activeCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center">
            {activeCount}
          </span>
        )}
      </div>
    </button>
  );
}
